import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { useSearchParams } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const contactSchema = z.object({
  name: z.string()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
  email: z.string()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  phone: z.string()
    .max(20, "Phone number must be less than 20 characters")
    .optional()
    .or(z.literal("")),
  message: z.string()
    .min(10, "Message must be at least 10 characters")
    .max(2000, "Message must be less than 2000 characters"),
});

type ContactFormData = z.infer<typeof contactSchema>;

const Contact = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  // Get the site key from Lovable Cloud secrets
  const [siteKey, setSiteKey] = useState<string>("");

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  useEffect(() => {
    // Fetch the public RECAPTCHA_SITE_KEY from backend
    const fetchSiteKey = async () => {
      try {
        const key = await supabase.functions.invoke('get-recaptcha-key');
        if (key.data?.siteKey) {
          setSiteKey(key.data.siteKey);
        }
      } catch (error) {
        console.error("Error fetching site key:", error);
      }
    };
    fetchSiteKey();
  }, []);

  useEffect(() => {
    const tourName = searchParams.get("tour");
    if (tourName) {
      form.setValue("message", `${t("contact.tourInquiry")}: ${tourName}\n\n`);
    }
  }, [searchParams, t, form]);

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    try {
      // Execute reCAPTCHA
      const recaptchaToken = await recaptchaRef.current?.executeAsync();
      
      if (!recaptchaToken) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "reCAPTCHA verification failed. Please refresh and try again.",
        });
        setIsSubmitting(false);
        return;
      }

      // Reset reCAPTCHA
      recaptchaRef.current?.reset();

      // Verify reCAPTCHA token
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke(
        "verify-recaptcha",
        {
          body: { token: recaptchaToken },
        }
      );

      if (verifyError || !verifyData?.success) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "reCAPTCHA verification failed. Please try again.",
        });
        setIsSubmitting(false);
        return;
      }

      // Send contact email
      const { error: emailError } = await supabase.functions.invoke(
        "send-contact-email",
        {
          body: data,
        }
      );

      if (emailError) {
        throw emailError;
      }

      toast({
        title: "Success!",
        description: "Message sent successfully! We'll get back to you soon.",
      });
      form.reset();
    } catch (error: any) {
      console.error("Contact form error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send message. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "TravelAgency",
      "name": "Shikwaru Adventures",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1 Paarl lane",
        "addressLocality": "Johannesburg",
        "addressCountry": "South Africa"
      },
      "telephone": "+27-73-2563514",
      "email": "info@shikwaru-adventures.com"
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact Shikwaru Adventures - Plan Your African Safari"
        description="Ready to start your African adventure? Get in touch with us today. Contact our expert team to plan your perfect safari in Namibia, Botswana, or Zimbabwe."
        keywords="contact safari operator, book African safari, safari inquiry, Shikwaru Adventures contact"
        structuredData={structuredData}
        canonical="/contact"
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-b from-secondary/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">{t("contact.title")}</h1>
            <p className="text-xl text-muted-foreground">
              {t("contact.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <MapPin className="mr-2 text-primary" />
                    {t("contact.location")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    1 Paarl lane<br />
                    Johannesburg - South Africa
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Phone className="mr-2 text-primary" />
                    {t("contact.phone")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    +27 73 2563514
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Mail className="mr-2 text-primary" />
                    {t("contact.email")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    info@shikwaru-adventures.com
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="mr-2 text-primary" />
                    {t("contact.officeHours")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    {t("contact.mondayFriday")}<br />
                    {t("contact.saturday")}<br />
                    {t("contact.sunday")}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">{t("contact.sendMessage")}</CardTitle>
                  <CardDescription>
                    {t("contact.formDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {t("contact.fullName")} *
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="John Doe"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {t("contact.emailAddress")} *
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="john@example.com"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("contact.phoneNumber")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="tel"
                                placeholder="+1 234 567 8900"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("contact.message")} *
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                rows={6}
                                placeholder={t("contact.messagePlaceholder")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {siteKey && (
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          size="invisible"
                          sitekey={siteKey}
                        />
                      )}

                      <Button
                        type="submit"
                        size="lg"
                        className="w-full"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? t("contact.sending") : t("contact.sendButton")}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
